import { Dispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { ActionTypes } from './action-types';
import { AuthActionCreators, IApplicationState } from '../';
import { IUserListResponse, IUser, IUserResponse, IRole, IBaseResponse, IGroup, IListItem, ICenterBase, IAuthRole } from '../../models';
import { IPost } from '../authorization';
import { OrganType } from '@nkr/enums/organ-type';

export interface IRoleListResponse {
    data: {
        success: boolean,
        message: string,
        results: IRole[]
    }
}

export interface IGroupListResponse {
    data: {
        success: boolean,
        message: string,
        results: IGroup[]
    }
}

export interface IVerifyAuthCodeResponse {
    data: {
        success: boolean,
        results: {
            isValidCode: boolean,
            isValidTime: boolean
        }
    }
}

export interface ILookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            userGroupList: IGroup[],
            roleList: IAuthRole[],
            centerList: ICenterBase[],
            micrositeCoachContactList: IListItem[],
            courierVendorList: IListItem[],
            organTypeList: IListItem[],
            opoList: IListItem[]
        }
    }
}

export interface IGroupLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            ticketGroupList: IListItem[],
        }
    }
}

export interface IAddUpdateGroupResponse {
    data: {
        success: boolean,
        message: string,
        results: IGroup
    }
}

export interface IGroupData {
    id: number,
    name: string,
    description: string,
    isSuperAdmin: boolean,
    canManageUsers: boolean,
    canUseCp: boolean,
    canAccessTicketing: boolean,
    accessTicketGroup: number,
    canChangeUsername: boolean,
    canChangePassword: boolean,
    canEditProfile: boolean,
    isCenter: boolean,
    isNkdo: boolean,
    canAddCenterUser: boolean,
    isLogisticsProvider: boolean,
    isXmKitVendor: boolean,
    isSerologyVendor: boolean,
    isRecipient: boolean,
    isDonor: boolean,
    isPhlebotomist: boolean,
    isNkrLab: boolean,
}

export const ActionCreators = {
    getUser: (id: number) => (dispatch: Dispatch<IApplicationState>): Promise<IUserResponse> => {
        let options = {
            url: __API__ + '/user/' + id
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IUserResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    getRoles: () => (dispatch: Dispatch<IApplicationState>): Promise<IRoleListResponse> => {
        let options = {
            url: __API__ + '/role'
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IRoleListResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    getGroups: () => async (dispatch: Dispatch<IApplicationState>): Promise<IGroupListResponse> => {
        let options = {
            url: __API__ + '/group'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    updatePassword: (userId: number, currentPassword: string, newPassword: string, confirmNewPassword: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: __API__ + '/user/' + userId + '/password',
            data: {
                currentPassword,
                newPassword,
                confirmNewPassword
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },
    sendAuthCode: (userId: number, isTwoFactor: boolean, mobileNumber: string, email: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: __API__ + '/user/' + userId + '/send-auth-code',
            data: {
                isTwoFactor,
                mobileNumber,
                email
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IBaseResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            })
    },
    verifyAuthCode: (userId: number, code: string, isTwoFactor: boolean, mobileNumber: string, email: string) => (dispatch: Dispatch<IApplicationState>): Promise<IVerifyAuthCodeResponse> => {
        let options = {
            url: __API__ + '/user/' + userId + '/verify-auth-code',
            data: {
                code,
                isTwoFactor,
                mobileNumber,
                email
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IVerifyAuthCodeResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            })
    },
    getLookupData: () => async (dispatch: Dispatch<IApplicationState>): Promise<ILookupDataResponse> => {
        let options = {
            url: __API__ + "/user/lookup-data"
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getGroupLookupData: () => async (dispatch: Dispatch<IApplicationState>): Promise<IGroupLookupDataResponse> => {
        let options = {
            url: __API__ + "/group/lookup-data"
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getUsers: (isAdmin: boolean, centerId: number, organType: OrganType) => (dispatch: Dispatch<IApplicationState>): Promise<IUserListResponse> => {
        let url = isAdmin ? __API__ + "/admin/user" : __API__ + `/center/${centerId}/user?${organType != null ? `organType=${organType}` : ''}`

        let options = {
            url: url
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IUserListResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    saveUser: (isAdmin: boolean, id: number, firstName: string, lastName: string, email: string, username: string, password: string, groupId: number, courierVendor: number, isMicrositeCoach: boolean, contactId: number, centerId: number, roles: number[], sendTicketNotification: boolean, isDeactivated: boolean, organType: OrganType, opoId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IUserResponse> => {
        let url = isAdmin ? __API__ + "/admin/user" : __API__ + `/center/${centerId}/user`

        if (id != 0) {
            url = url + `/${id}`
        }

        let options = {
            url,
            data: {
                id,
                firstName,
                lastName,
                email,
                username,
                password,
                groupId,
                courierVendor,
                isMicrositeCoach,
                contactId,
                centerId,
                sendTicketNotification,
                isDeactivated,
                roles,
                organType,
                opoId
            }
        };

        if (id == 0) {
            return await dispatch(AuthActionCreators.apiPost(options));
        } else {
            return await dispatch(AuthActionCreators.apiPut(options));
        }
    },
    deleteUser: (isAdmin: boolean, centerId: number, userId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let url = isAdmin ? __API__ + `/admin/user/${userId}` : __API__ + `/center/${centerId}/user/${userId}`

        let options = {
            url
        };

        return await dispatch(AuthActionCreators.apiDelete(options));
    },

    updatePasswordExpiration: (isAdmin: boolean, centerId: number, userId: number, activate: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let url = isAdmin ? __API__ + '/admin/user/' + userId + '/update-password-expiration?activate=' + activate : __API__ + `/center/${centerId}/user/` + userId + '/update-password-expiration?activate=' + activate

        let options = {
            url
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    unblockUser: (isAdmin: boolean, centerId: number, userId: number, ipAddress: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let url = isAdmin ? __API__ + '/admin/user/' + userId + '/unblock?ipAddress=' + ipAddress : __API__ + `/center/${centerId}/user/` + userId + '/unblock?ipAddress=' + ipAddress

        let options = {
            url
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },


    forceUnlockUser: (isAdmin: boolean, centerId: number, userId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let url = isAdmin ? __API__ + `/admin/user/${userId}/force-unlock` : __API__ + `/center/${centerId}/user/${userId}/force-unlock`

        let options = {
            url
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    forceLockUser: (isAdmin: boolean, centerId: number, userId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let url = isAdmin ? __API__ + `/admin/user/${userId}/force-lock` : __API__ + `/center/${centerId}/user/${userId}/force-lock`

        let options = {
            url
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    setUsers: (list: IUser[]) => {
        return {
            type: ActionTypes.SET_USERS,
            list
        };
    },
    addUser: (user: IUser) => {
        return {
            type: ActionTypes.ADD_USER,
            user
        };
    },
    updateUser: (user: IUser) => {
        return {
            type: ActionTypes.UPDATE_USER,
            user
        };
    },
    setRoles: (list: IRole[]) => {
        return {
            type: ActionTypes.SET_ROLES,
            list
        };
    },
    setGroups: (list: IGroup[]) => {
        return {
            type: ActionTypes.SET_GROUPS,
            list
        };
    },
    updateGroup: (data: IGroupData) => async (dispatch: Dispatch<IApplicationState>): Promise<IAddUpdateGroupResponse> => {
        let options = {
            url: __API__ + '/group',
            data
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },
    addGroup: (data: IGroupData) => async (dispatch: Dispatch<IApplicationState>): Promise<IAddUpdateGroupResponse> => {
        let options = {
            url: __API__ + '/group',
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    checkInviteEmailAvailable: (emailAddress: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/user/check-invite-email-available?email=${encodeURI(emailAddress)}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    }
}